import { X } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { displayName, lifeSpan } from '../../model/people';
import { search } from '../../model/search';
import type { Id, Tree } from '../../model/types';
import { Avatar, useDropdownPlace } from './common';

/** Searchable person combobox. */
export function PersonPicker({
  tree,
  value,
  onChange,
  exclude = [],
  placeholder = 'Search people…',
  autoFocus,
  ariaLabel,
  onCreate,
}: {
  tree: Tree;
  value: Id | null;
  onChange: (id: Id | null) => void;
  exclude?: Id[];
  placeholder?: string;
  autoFocus?: boolean;
  ariaLabel?: string;
  /** Show a "create new person" option that receives the typed text. */
  onCreate?: (text: string) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  // Clicking the chosen person searches for someone to replace them; leaving without a pick keeps them.
  const [replacing, setReplacing] = useState(false);
  const current = value && tree.people[value] ? tree.people[value] : null;
  const [active, setActive] = useState(0);
  const listId = useId();
  const results = useMemo(() => {
    const ex = new Set(exclude);
    if (value) ex.add(value);
    // Enough results that leaving out the excluded (a group's members, say) still leaves some.
    const list = q.trim() ? search(tree, q, { limit: 40 + ex.size }).map((r) => r.person) : Object.values(tree.people).slice(0, 40 + ex.size);
    return list.filter((p) => !ex.has(p.id)).slice(0, 20);
  }, [tree, q, exclude, value]);
  const createIdx = onCreate && q.trim() ? results.length : -1;
  const total = results.length + (createIdx >= 0 ? 1 : 0);
  const inputRef = useRef<HTMLInputElement>(null);
  // The list floats above everything (fixed, in a portal) so dialogs and scroll areas can't clip it.
  const shown = open && total > 0;
  const place = useDropdownPlace(inputRef, shown, total);

  if (current && !replacing) {
    const p = current;
    return (
      <div className="picker-selected">
        <button type="button" className="picker-current" onClick={() => setReplacing(true)} title="Choose someone else" aria-label={`${displayName(p)}. Choose someone else`}>
          <Avatar person={p} size="sm" />
          <span className="name">{displayName(p)}</span>
          <span className="faint small-text">{lifeSpan(p)}</span>
        </button>
        <button type="button" className="btn icon small ghost" onClick={() => onChange(null)} aria-label="Clear selection">
          <X size={14} />
        </button>
      </div>
    );
  }

  const choose = (i: number) => {
    if (i === createIdx && onCreate) {
      onCreate(q.trim());
      setQ('');
      setOpen(false);
      return;
    }
    const p = results[i];
    if (!p) return;
    onChange(p.id);
    setQ('');
    setOpen(false);
    setReplacing(false);
  };

  return (
    <div className="picker">
      <input
        ref={inputRef}
        className="input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel ?? placeholder}
        aria-activedescendant={open && total ? `${listId}-${active}` : undefined}
        placeholder={replacing && current ? `Replace ${displayName(current)}…` : placeholder}
        value={q}
        autoFocus={autoFocus || replacing}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() =>
          setTimeout(() => {
            setOpen(false);
            setReplacing(false);
            setQ('');
          }, 150)
        }
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(total - 1, a + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === 'Enter' && open && total) {
            e.preventDefault();
            choose(active);
          } else if (e.key === 'Escape' && replacing) {
            e.stopPropagation();
            setReplacing(false);
            setQ('');
          } else if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      />
      {shown && place && createPortal(
        <div className="picker-list" role="listbox" id={listId} style={place}>
          {results.map((p, i) => (
            <div
              key={p.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className="picker-item"
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <Avatar person={p} size="sm" />
              <div>
                <div className="name">{displayName(p)}</div>
                <div className="meta">{[lifeSpan(p), p.pronouns].filter(Boolean).join(' · ')}</div>
              </div>
            </div>
          ))}
          {createIdx >= 0 && (
            <div
              id={`${listId}-${createIdx}`}
              role="option"
              aria-selected={active === createIdx}
              className="picker-item"
              onMouseDown={(e) => {
                e.preventDefault();
                choose(createIdx);
              }}
              onMouseEnter={() => setActive(createIdx)}
            >
              <span className="avatar sm" style={{ background: 'var(--accent)' }}>+</span>
              <div className="name">Create “{q.trim()}”</div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
